import { formatCurrency } from 'shared/helpers/utils';

import type { PosFeeItem } from './use-pos-fees-config';

type DeliverySchedule = 'asap' | 'scheduled';

type FeeTotals = {
  flat: number;
  percent: number;
  count: number;
};

const appliesToSchedule = (fee: PosFeeItem, schedule: DeliverySchedule): boolean =>
  fee.appliesToAllOrderTypes || fee.orderTypes.includes(schedule);

// The POS only applies a threshold-gated fee when the cart subtotal falls inside
// [orderMinimum, orderMaximum], so pre-cart labels can't claim such fees exactly.
const isThresholdGated = (fee: PosFeeItem): boolean => fee.orderMinimum != null || fee.orderMaximum != null;

const withinThreshold = (fee: PosFeeItem, subtotal: number): boolean =>
  (fee.orderMinimum == null || subtotal >= fee.orderMinimum) &&
  (fee.orderMaximum == null || subtotal <= fee.orderMaximum);

/**
 * Sums the fees that apply to a scheduling option. The POS applies EVERY matching automatic
 * fee (they stack), so a single-fee "first match" would under-report stores with more than
 * one applicable fee. Flat and percent amounts are totaled separately since they can't be
 * combined without a cart subtotal.
 */
function totalsForSchedule(fees: PosFeeItem[], schedule: DeliverySchedule): FeeTotals {
  return fees
    .filter((f) => appliesToSchedule(f, schedule))
    .reduce(
      (acc, f) => ({
        flat: acc.flat + (f.calculationMethod === 'flat' ? f.calculationValue : 0),
        percent: acc.percent + (f.calculationMethod === 'percent' ? f.calculationValue : 0),
        count: acc.count + 1,
      }),
      { flat: 0, percent: 0, count: 0 }
    );
}

/** Renders stacked totals: "$5.00", "5%", or the combined "$5.00 + 5%". */
function formatTotals({ flat, percent }: FeeTotals): string {
  if (flat > 0 && percent > 0) return `${formatCurrency(flat)} + ${percent}%`;
  if (percent > 0) return `${percent}%`;
  return formatCurrency(flat);
}

/**
 * Returns the delivery fee label for ONE scheduling option (ASAP or Scheduled) before the
 * delivery zone is known, so a shopper sees the fee for that choice before selecting it.
 * Only usable when every delivery fee is all-zones: if any fee is zone-scoped, the true
 * total depends on the (not yet resolved) zone, so we return null rather than under-report
 * (caller omits the label; the zone-resolved variant takes over after cart pricing).
 */
export function formatPosDeliveryFeeForSchedule(deliveryFees: PosFeeItem[], schedule: DeliverySchedule): string | null {
  if (deliveryFees.some((f) => !f.appliesToAllZones)) return null;
  // Threshold-gated fees depend on the (not yet priced) cart subtotal; the zone/subtotal-aware
  // variant takes over with the exact figure once the cart is priced.
  if (deliveryFees.some((f) => appliesToSchedule(f, schedule) && isThresholdGated(f))) return null;

  const totals = totalsForSchedule(deliveryFees, schedule);
  if (totals.count === 0) return null;

  return `(${formatTotals(totals)} fee)`;
}

/**
 * Returns the numeric flat delivery fee total (dollars) for a scheduling option, used to pick
 * the cheaper default scheduling option before a cart is priced. Comparable only when the
 * applicable fees are all-zones and flat: percent fees can't be compared without a cart
 * subtotal, and zone-scoped fees depend on the unresolved zone — both return null (caller
 * keeps the existing default).
 */
export function getPosDeliveryFlatFeeForSchedule(
  deliveryFees: PosFeeItem[],
  schedule: DeliverySchedule
): number | null {
  if (deliveryFees.some((f) => !f.appliesToAllZones)) return null;
  // A threshold-gated fee may or may not apply to the eventual cart, so the options
  // aren't comparable pre-cart.
  if (deliveryFees.some((f) => appliesToSchedule(f, schedule) && isThresholdGated(f))) return null;

  const totals = totalsForSchedule(deliveryFees, schedule);
  if (totals.count === 0 || totals.percent > 0) return null;

  return totals.flat;
}

/**
 * Returns the EXACT delivery fee label for a scheduling option once the POS pricing engine
 * has resolved the delivery zone(s) for the cart address (via the price-cart response's
 * resolvedZoneIds). Sums every fee that covers all zones or one of the resolved zones AND
 * targets this schedule — matching what the POS will actually charge.
 * Returns null when no fee applies (caller omits the label).
 */
export function formatPosDeliveryFeeForZone(
  deliveryFees: PosFeeItem[],
  resolvedZoneIds: number[],
  schedule: DeliverySchedule,
  subtotal: number | null = null
): string | null {
  let applicable = deliveryFees.filter(
    (f) => f.appliesToAllZones || f.zones.some((z) => resolvedZoneIds.includes(z.zoneId))
  );

  // The POS applies threshold-gated fees only when the subtotal is inside
  // [orderMinimum, orderMaximum]. With a priced cart (subtotal in dollars, same units as the
  // thresholds), apply the gate exactly; without one we can't claim exactness, so omit the
  // label rather than show a fee the order summary may not charge.
  if (subtotal == null) {
    if (applicable.some((f) => appliesToSchedule(f, schedule) && isThresholdGated(f))) return null;
  } else {
    applicable = applicable.filter((f) => withinThreshold(f, subtotal));
  }

  const totals = totalsForSchedule(applicable, schedule);
  if (totals.count === 0) return null;

  return `(${formatTotals(totals)} fee)`;
}

/**
 * Formats POS-sourced delivery fees as a single summary for the order-type
 * (pickup vs delivery) selector, shown before a cart is priced. Collapses the
 * ASAP and Scheduled totals into one range so the shopper sees the spread at a
 * glance (the per-option breakdown lives on the Time selector instead).
 * Parenthesized by default (for the inline order-type selector); pass `{ parenthesized: false }`
 * for surfaces like the menu header / store-info sidebar.
 * - Same total for both options → "($5.00 fee)" / "$5.00 fee".
 * - Different totals → "($3.00–$5.00 fee)" / "$3.00–$5.00 fee".
 * - Any zone-scoped fee, or flat+percent mixes that don't range cleanly →
 *   "Delivery fee varies by location" (the exact fee depends on address/cart).
 *
 * Returns null when there are no delivery fees so callers can omit the label.
 */
export function formatPosDeliveryFees(
  deliveryFees: PosFeeItem[],
  { parenthesized = true }: { parenthesized?: boolean } = {}
): string | null {
  if (deliveryFees.length === 0) return null;

  // Zone-scoped fees stack on top of any all-zones fees for the covered zones, so no single
  // pre-address number or range is truthful — fall back to the generic label.
  if (deliveryFees.some((f) => !f.appliesToAllZones)) {
    return 'Delivery fee varies by location';
  }

  // Threshold-gated fees apply only within a subtotal window, so a single pre-cart number
  // would be wrong for carts outside it (matches the legacy fee-tier phrasing).
  if (deliveryFees.some(isThresholdGated)) {
    return 'Delivery fee varies by order size';
  }

  const asap = totalsForSchedule(deliveryFees, 'asap');
  const scheduled = totalsForSchedule(deliveryFees, 'scheduled');
  if (asap.count === 0 && scheduled.count === 0) return null;

  // One option free, the other not: a single number or range would misstate one of them.
  if (asap.count === 0 || scheduled.count === 0) {
    return 'Delivery fee varies by location';
  }

  const wrap = (core: string): string => (parenthesized ? `(${core})` : core);
  const totals = [asap, scheduled];
  const allFlat = totals.every((t) => t.percent === 0);
  const allPercent = totals.every((t) => t.flat === 0);

  if (allFlat) {
    const values = totals.map((t) => t.flat);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return wrap(min === max ? `${formatCurrency(min)} fee` : `${formatCurrency(min)}–${formatCurrency(max)} fee`);
  }

  if (allPercent) {
    const values = totals.map((t) => t.percent);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return wrap(min === max ? `${min}% fee` : `${min}%–${max}% fee`);
  }

  // Flat-and-percent composites don't collapse into a readable range; if both options cost
  // the same we can still show the exact composite, otherwise use the generic label.
  const asapLabel = formatTotals(asap);
  if (asapLabel === formatTotals(scheduled)) {
    return wrap(`${asapLabel} fee`);
  }
  return 'Delivery fee varies by location';
}
